import type { DatasetShape } from '../types';

// How many meter identifiers to name before summarising the rest. Enough to be
// actionable when re-requesting data, short enough to stay a readable sentence.
const MAX_NAMED_METERS = 6;

/**
 * Builds the user-facing refusal for a sheet that interleaves several meters.
 *
 * This deliberately does NOT pick a meter. The two meters in the VEC
 * net-metering export are a residential service meter and a solar meter, and
 * both look alike to software: each carries a full year of hourly readings,
 * each sums to roughly 10,000 kWh, and each goes negative during export. There
 * is no signal in the file identifying which one feeds the panel being
 * evaluated — only the contractor on site knows that.
 *
 * So the file is refused with an explanation rather than guessed at. The prior
 * behaviour guessed: it merged both meters and let the 'maximum' conflict
 * strategy resolve each duplicated timestamp, returning a plausible peak with
 * no warning at all. On this fixture that peak happens to be correct, purely
 * because the max across both meters landed on the right one; on a service
 * where the other meter reads higher, the same path returns a wrong number just
 * as silently. A number that is usually right is worse than a refusal, because
 * it goes onto a permit unchallenged.
 */
export function describeMultiMeter(shape: DatasetShape): string {
  const meters = shape.meterIdValues ?? [];
  const named = meters.slice(0, MAX_NAMED_METERS).join(', ');
  const rest = meters.length - MAX_NAMED_METERS;
  const list = rest > 0 ? `${named}, and ${rest} more` : named;

  return (
    `This file contains interval readings from ${meters.length} different meters ` +
    `(${list}). TrueLoad cannot determine which meter serves the panel you are ` +
    `evaluating, and combining them would report the highest reading across all ` +
    `of them as if it came from one service. ` +
    `Please upload data for a single meter — re-export from your utility with ` +
    `only the meter for this service address selected.`
  );
}
